import React, { useState, useEffect, useCallback } from "react";
import { Users2, Search, X, Ban, RotateCcw, Trash2, ShieldCheck, Mail, CalendarDays, KeyRound, Clock3, UserRound, CheckSquare, Square, Download, Activity, Phone, MessageCircle, ChevronDown, ChevronUp, Video, CalendarPlus } from "lucide-react";
import { LogDetailModal } from "./lib/LogDetail";
import { openNativeMeeting } from "./modules/NativeMeeting";
import { toast } from "sonner";
import { ccInput, ccCard } from "./care/ui";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = { master: "Master", daycare_admin: "Facility Owner", staff: "Staff", caregiver: "Caregiver", parent: "Parent" };
const ROLE_FILTERS = ["all", "master", "daycare_admin", "staff", "caregiver", "parent"];
const STATUS_FILTERS = ["all", "active", "suspended", "invited"];

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtDateTime = (v) =>
  v ? new Date(v).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "Never";

const StatusBadge = ({ status }) => (
  <span
    data-testid={`user-status-${status}`}
    className={`px-2.5 py-1 text-[10px] font-extrabold uppercase rounded-full border ${
      status === "suspended"
        ? "bg-rose-50 border-rose-200 text-rose-500"
        : status === "invited"
        ? "bg-[#FFF2D1] border-[#F5E3B3] text-[#A08244]"
        : "bg-[#E2F4E1] border-[#BEE3BD] text-[#2F6031]"
    }`}
  >
    {status || "active"}
  </span>
);

const LIVE_LOC_OPTIONS = [["header", "Header"], ["profile", "Profile"], ["store", "Store"], ["care", "Care Connections"], ["directory", "Provider Directory"]];

const UserProfileModal = ({ user, onClose, onAction, currentUserEmail, canManageLive = false }) => {
  const [liveOn, setLiveOn] = useState(Boolean(user?.live_public_approved));
  const [liveLocs, setLiveLocs] = useState(user?.live_locations || LIVE_LOC_OPTIONS.map((l) => l[0]));
  const [verifDocs, setVerifDocs] = useState(null);

  const toggleDocVerify = async (docId) => {
    try {
      const res = await fetch(`${API}/api/master/caregiver-verifications/${encodeURIComponent(user.email)}/docs/${docId}`, { method: "POST", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || "Could not update document.");
        return;
      }
      setVerifDocs((prev) => prev && { ...prev, verification_status: data.verification_status, docs: prev.docs.map((d) => (d.id === docId ? { ...d, verified: data.verified } : d)) });
      toast.success(data.verified ? "Document verified." : "Document unverified.");
    } catch {
      toast.error("Network error — could not update document.");
    }
  };
  const toggleLive = async () => {
    const next = !liveOn;
    setLiveOn(next);
    try {
      const res = await fetch(`${API}/api/master/staff/${user.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ live_public_approved: next }),
      });
      if (!res.ok) throw new Error();
      toast.success(next ? `${user.name} can now host public live events.` : `Public live turned off for ${user.name}.`);
    } catch {
      setLiveOn(!next);
      toast.error("Could not update the Go Live switch.");
    }
  };
  const toggleLoc = async (key) => {
    const prev = liveLocs;
    const next = liveLocs.includes(key) ? liveLocs.filter((l) => l !== key) : [...liveLocs, key];
    setLiveLocs(next);
    try {
      const res = await fetch(`${API}/api/master/live-locations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: user.email, locations: next }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Go Live from ${key} ${next.includes(key) ? "enabled" : "cut off"} for ${user.name}.`);
    } catch {
      setLiveLocs(prev);
      toast.error("Could not update Go Live locations.");
    }
  };
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activity, setActivity] = useState(null);
  const [logSel, setLogSel] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [schedOpen, setSchedOpen] = useState(false);
  const [pickerQ, setPickerQ] = useState("");
  const [pickerUsers, setPickerUsers] = useState([]);
  useEffect(() => {
    if (!schedOpen) return;
    fetch(`${API}/api/meetings/invitees`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { users: [] }))
      .then((d) => setPickerUsers(d.users || []))
      .catch(() => {});
  }, [schedOpen]);
  const [sched, setSched] = useState({ title: "", date: "", time: "" });

  useEffect(() => {
    setLiveOn(Boolean(user?.live_public_approved));
    setLiveLocs(user?.live_locations || LIVE_LOC_OPTIONS.map((l) => l[0]));
    setVerifDocs(null);
    setActivity(null);
    setConfirmDelete(false);
    setSchedOpen(false);
    setSched({ title: "", date: "", time: "" });
    if (!user) return;
    if (canManageLive && user.role === "caregiver") {
      fetch(`${API}/api/master/caregiver-verifications`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : []))
        .then((list) => setVerifDocs(list.find((c) => c.email === user.email) || { docs: [], verification_status: "unverified", claimed_credentials: [] }))
        .catch(() => {});
    }
    fetch(`${API}/api/directory/users/${user.id}/activity`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { activities: [] }))
      .then((d) => setActivity(d.activities || []))
      .catch(() => setActivity([]));
    fetch(`${API}/api/directory/meetings?with_id=${user.id}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { meetings: [] }))
      .then((d) => setMeetings(d.meetings || []))
      .catch(() => setMeetings([]));
  }, [user]);

  if (!user) return null;
  const isSelf = user.email === currentUserEmail;

  const act = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
      setConfirmDelete(false);
    }
  };

  const messageUser = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/chat/threads`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not open conversation");
      sessionStorage.setItem("nt_view", "care");
      window.location.assign("/");
    } catch (err) {
      toast.error(err.message);
      setBusy(false);
    }
  };

  const videoCall = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/directory/users/${user.id}/video-call`, { method: "POST", credentials: "include" });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not start video call");
      if (body.roomSlug) openNativeMeeting(body.roomSlug);
      toast.success(`Video room live — ${user.name || user.email} got an email + push invite.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const scheduleMeeting = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/directory/meetings`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: sched.title, date: sched.date, time: sched.time, participant_ids: Array.from(new Set([user.id, ...(sched.user_ids || [])])) }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not schedule meeting");
      toast.success(`Meeting set for ${body.date} at ${body.time} — email + push sent and it's on the calendar.`);
      setSched({ title: "", date: "", time: "" });
      setSchedOpen(false);
      setMeetings((m) => [...m, body]);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const rows = [
    { icon: Mail, label: "Email", value: user.email, testId: "profile-email" },
    { icon: UserRound, label: "Role", value: ROLE_LABELS[user.role] || user.role, testId: "profile-role" },
    { icon: ShieldCheck, label: "Account Status", value: user.status || "active", testId: "profile-status" },
    { icon: CalendarDays, label: "Joined", value: fmtDate(user.created_at), testId: "profile-joined" },
    { icon: Clock3, label: "Last Login", value: fmtDateTime(user.last_login), testId: "profile-last-login" },
    { icon: KeyRound, label: "Sign-in Method", value: user.auth_provider === "google" ? "Google" : "Email & Password", testId: "profile-auth-provider" },
  ];
  if (user.phone) rows.splice(1, 0, { icon: Phone, label: "Phone", value: user.phone, testId: "profile-phone" });

  return (
    <div className="fixed inset-0 z-50 bg-[#374151]/30 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="user-profile-modal" onClick={onClose}>
      <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-md w-full p-7 shadow-xl animate-modal-in max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-5 border-b border-[#FCE6E9] pb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#374151] overflow-hidden flex items-center justify-center text-white text-base font-black font-heading shrink-0">
              {user.avatar ? <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" /> : (user.name || "?").charAt(0)}
            </div>
            <div>
              <h3 className="text-base font-extrabold font-heading text-[#374151]" data-testid="profile-name">{user.name || "Unnamed"}</h3>
              <div className="mt-1"><StatusBadge status={user.status} /></div>
            </div>
          </div>
          <button onClick={onClose} data-testid="profile-close-btn" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Immediate contact actions — available on every account, including admins */}
        <div className="grid grid-cols-4 gap-2 mb-5" data-testid="profile-contact-actions">
          {user.phone ? (
            <a href={`tel:${user.phone}`} data-testid="profile-call-btn" className="flex flex-col items-center gap-1 p-3 bg-[#E2F4E1] border border-[#BEE3BD] rounded-2xl text-[#2F6031] hover:bg-[#C9EAC7] transition-colors !no-underline">
              <Phone className="w-4 h-4" />
              <span className="text-[10px] font-extrabold uppercase tracking-wide">Call</span>
            </a>
          ) : (
            <span data-testid="profile-call-disabled" className="flex flex-col items-center gap-1 p-3 bg-gray-50 border border-gray-200 rounded-2xl text-gray-300 cursor-not-allowed" title="No phone number on file">
              <Phone className="w-4 h-4" />
              <span className="text-[10px] font-extrabold uppercase tracking-wide">Call</span>
            </span>
          )}
          {user.phone ? (
            <a href={`sms:${user.phone}`} data-testid="profile-text-btn" className="flex flex-col items-center gap-1 p-3 bg-[#FFF2D1] border border-[#F5E3B3] rounded-2xl text-[#A08244] hover:bg-[#FCEABB] transition-colors !no-underline">
              <MessageCircle className="w-4 h-4" />
              <span className="text-[10px] font-extrabold uppercase tracking-wide">Text</span>
            </a>
          ) : (
            <span data-testid="profile-text-btn" className="flex flex-col items-center gap-1 p-3 bg-gray-50 border border-gray-200 rounded-2xl text-gray-300 cursor-not-allowed" title="No phone number on file">
              <MessageCircle className="w-4 h-4" />
              <span className="text-[10px] font-extrabold uppercase tracking-wide">Text</span>
            </span>
          )}
          <button onClick={messageUser} disabled={busy || isSelf} data-testid="profile-message-btn" className="flex flex-col items-center gap-1 p-3 bg-[#E6F2FA] border border-[#B3E0F2] rounded-2xl text-[#3D7E9E] hover:bg-[#D2E9F5] transition-colors disabled:opacity-50">
            <MessageCircle className="w-4 h-4" />
            <span className="text-[10px] font-extrabold uppercase tracking-wide">Message</span>
          </button>
          <a href={`mailto:${user.email}`} data-testid="profile-email-btn" className="flex flex-col items-center gap-1 p-3 bg-[#FCE6E9] border border-[#F8C8DC] rounded-2xl text-[#B04A6B] hover:bg-[#F8C8DC] transition-colors !no-underline">
            <Mail className="w-4 h-4" />
            <span className="text-[10px] font-extrabold uppercase tracking-wide">Email</span>
          </a>
        </div>

        {/* Networking: video calls, scheduled meetings, app downloads */}
        {canManageLive && (
          <>
          <div className="mb-5 flex items-center justify-between gap-3 p-3.5 bg-rose-50/50 border border-rose-100 rounded-2xl" data-testid="profile-live-switch">
            <div>
              <p className="text-[11px] font-extrabold text-[#374151]">Public Go Live</p>
              <p className="text-[10px] text-[#9CA3AF] font-medium">{liveOn ? "Approved — can host public homepage events" : "Off — public homepage events locked"}</p>
            </div>
            <button
              type="button"
              onClick={toggleLive}
              data-testid="profile-live-toggle"
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${liveOn ? "bg-[#7CC98F]" : "bg-gray-200"}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${liveOn ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
          {canManageLive && user.role !== "master" && (
            <div className="mt-2 p-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl dark:bg-[#273244] dark:border-[#374457]" data-testid="live-locations-panel">
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF] mb-2">Go Live locations — cut on/off per page</p>
              <div className="flex flex-wrap gap-1.5">
                {LIVE_LOC_OPTIONS.map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleLoc(key)}
                    data-testid={`live-loc-${key}`}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-extrabold border transition-colors ${
                      liveLocs.includes(key) ? "bg-[#E2F4E1] border-[#BEE3BD] text-[#2F6031]" : "bg-gray-50 border-gray-200 text-gray-400"
                    }`}
                  >
                    {label} {liveLocs.includes(key) ? "On" : "Off"}
                  </button>
                ))}
              </div>
            </div>
          )}
          </>
        )}
        {/* Caregiver document verification (master only) */}
        {canManageLive && user.role === "caregiver" && (
          <div className="mb-5 p-3.5 bg-amber-50/60 border border-amber-100 rounded-2xl" data-testid="profile-verification">
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-[11px] font-extrabold text-[#374151]">Document Verification</p>
              {verifDocs && (
                <span data-testid="profile-verification-status" className={`text-[10px] font-extrabold px-2 py-1 rounded-full ${verifDocs.verification_status === "verified" ? "bg-[#E2F4E1] text-[#2F6031]" : "bg-[#F5E3B3]/50 text-[#8a6d1a]"}`}>
                  {verifDocs.verification_status === "verified" ? "Verified" : "Unverified"}
                </span>
              )}
            </div>
            {!verifDocs ? (
              <p className="text-[10px] text-[#9CA3AF] font-medium">Loading…</p>
            ) : verifDocs.docs.length === 0 ? (
              <p className="text-[10px] text-[#9CA3AF] font-medium">No documents submitted yet — profile stays Unverified until all required documents and licenses are verified.</p>
            ) : (
              <div className="space-y-1.5">
                {verifDocs.docs.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-2 text-[11px]">
                    <span className="font-bold text-[#374151] truncate">{d.doc_type === "government_id" ? "Government ID" : d.credential_type || "Credential"} — {d.file_name}</span>
                    <button type="button" onClick={() => toggleDocVerify(d.id)} data-testid={`profile-doc-toggle-${d.id}`} className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] font-extrabold transition-colors ${d.verified ? "bg-[#7CC98F] text-white" : "bg-white border border-[#F5E3B3] text-[#A08244] hover:bg-[#FFF2D1]"}`}>
                      {d.verified ? "Verified" : "Mark Verified"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="mb-5 p-4 bg-[#E6F2FA]/40 border border-[#B3E0F2] rounded-2xl" data-testid="profile-networking">
          <p className="flex items-center gap-2 text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wide mb-2.5">
            <Video className="w-3.5 h-3.5 text-[#5CB3DC]" /> Networking & Meetings
          </p>
          {!isSelf && (
            <>
              <div className="grid grid-cols-2 gap-2 mb-2.5">
                <button onClick={videoCall} disabled={busy} data-testid="profile-video-call-btn" className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-[#5CB3DC] hover:bg-[#3D7E9E] text-white rounded-full text-[11px] font-extrabold transition-colors disabled:opacity-50">
                  <Video className="w-3.5 h-3.5" /> Video Call
                </button>
                <button onClick={() => setSchedOpen((o) => !o)} data-testid="profile-schedule-btn" className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-white hover:bg-[#FFF2D1] border border-[#F5E3B3] text-[#A08244] rounded-full text-[11px] font-extrabold transition-colors">
                  <CalendarPlus className="w-3.5 h-3.5" /> Schedule Meeting
                </button>
              </div>
              {schedOpen && (
                <form onSubmit={scheduleMeeting} className="space-y-2 mb-2.5" data-testid="profile-schedule-form">
                  <input type="text" required value={sched.title} onChange={(e) => setSched({ ...sched, title: e.target.value })} placeholder="Meeting title" className={ccInput} data-testid="schedule-title" />
                  <div className="grid grid-cols-2 gap-2">
                    <input type="date" required value={sched.date} onChange={(e) => setSched({ ...sched, date: e.target.value })} className={ccInput} data-testid="schedule-date" />
                    <input type="time" required value={sched.time} onChange={(e) => setSched({ ...sched, time: e.target.value })} className={ccInput} data-testid="schedule-time" />
                  </div>
                  <input type="text" value={pickerQ} onChange={(e) => setPickerQ(e.target.value)} placeholder="Search members to add…" className={ccInput} data-testid="schedule-member-search" />
                  <div className="max-h-32 overflow-y-auto space-y-1 bg-white/60 border border-[#F5E3B3] rounded-xl p-1.5" data-testid="schedule-member-list">
                    {pickerUsers
                      .filter((p) => p.id !== user.id && (!pickerQ.trim() || (p.name || "").toLowerCase().includes(pickerQ.toLowerCase()) || p.email.toLowerCase().includes(pickerQ.toLowerCase())))
                      .slice(0, 25)
                      .map((p) => (
                        <label key={p.id} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-white cursor-pointer" data-testid={`schedule-member-${p.id}`}>
                          <input
                            type="checkbox"
                            checked={(sched.user_ids || []).includes(p.id)}
                            onChange={(e) => setSched((s) => ({ ...s, user_ids: e.target.checked ? [...(s.user_ids || []), p.id] : (s.user_ids || []).filter((x) => x !== p.id) }))}
                            className="w-3.5 h-3.5 accent-[#5CB3DC]"
                          />
                          <span className="text-[11px] font-bold text-[#374151] flex-1 truncate">{p.name || p.email} <span className="font-semibold text-[#9CA3AF]">· {p.role.replace("_", " ")}</span></span>
                          <span className={`text-[9px] font-extrabold uppercase px-2 py-0.5 rounded-full ${p.mode === "request" ? "bg-[#F5E3B3]/50 text-[#8a6d1a]" : "bg-[#E2F4E1] text-[#2F6031]"}`}>
                            {p.mode === "request" ? "Request" : "Schedule"}
                          </span>
                        </label>
                      ))}
                    {pickerUsers.length === 0 && <p className="text-[10px] text-[#9CA3AF] font-semibold text-center py-2">No members in your scheduling scope.</p>}
                  </div>
                  <button type="submit" disabled={busy} className="w-full px-3 py-2.5 bg-[#374151] hover:bg-[#1F2937] text-white rounded-full text-[11px] font-extrabold transition-colors disabled:opacity-50" data-testid="schedule-submit">
                    {busy ? "Scheduling…" : "Schedule / Request — email + push invites, auto-added to calendar"}
                  </button>
                </form>
              )}
              {meetings.length > 0 && (
                <div className="space-y-1.5 mb-2.5" data-testid="profile-meetings">
                  {meetings.map((m) => (
                    <div key={m.id} className="flex items-center justify-between gap-2 p-2.5 bg-white border border-[#B3E0F2] rounded-xl" data-testid={`profile-meeting-${m.id}`}>
                      <div className="min-w-0">
                        <p className="text-[11px] font-extrabold text-[#374151] truncate">{m.title}</p>
                        <p className="text-[10px] text-[#9CA3AF] font-bold">{m.date} · {m.time}</p>
                      </div>
                      <a href={m.link} target="_blank" rel="noreferrer" className="px-2.5 py-1 bg-[#5CB3DC] text-white text-[9px] font-extrabold uppercase rounded-full shrink-0 !no-underline" data-testid={`profile-meeting-join-${m.id}`}>Join</a>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <div className="grid grid-cols-2 gap-2" data-testid="profile-app-links">
            <a href="/" target="_blank" rel="noreferrer" data-testid="profile-download-nesttrack" className="flex items-center justify-center gap-1.5 px-3 py-2 bg-[#FCE6E9] border border-[#F8C8DC] text-[#B04A6B] rounded-full text-[10px] font-extrabold !no-underline hover:bg-[#F8C8DC] transition-colors">
              <Download className="w-3 h-3" /> NestTrack App
            </a>
            <p className="text-[10px] text-[#9CA3AF] font-semibold text-center" data-testid="profile-video-note">Video calls run natively inside NestTrack — no other app needed.</p>
          </div>
        </div>

        <div className="space-y-2 mb-5" data-testid="profile-details">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center justify-between gap-3 p-3 bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl" data-testid={r.testId}>
              <span className="flex items-center gap-2 text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wide">
                <r.icon className="w-3.5 h-3.5 text-[#E5A0BC]" /> {r.label}
              </span>
              <span className="text-xs font-extrabold text-[#374151] text-right break-all">{r.value}</span>
            </div>
          ))}
        </div>

        {/* Activity timeline */}
        <div className="mb-5" data-testid="profile-activity">
          <p className="flex items-center gap-2 text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wide mb-2.5">
            <Activity className="w-3.5 h-3.5 text-[#5CB3DC]" /> Recent Activity
          </p>
          {activity === null ? (
            <p className="text-[11px] text-[#9CA3AF] font-medium py-2" data-testid="activity-loading">Loading activity...</p>
          ) : activity.length === 0 ? (
            <p className="text-[11px] text-[#9CA3AF] font-medium py-2" data-testid="activity-empty">No recorded activity yet.</p>
          ) : (
            <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1" data-testid="activity-list">
              {activity.map((a, i) => (
                <button key={i} onClick={() => setLogSel(a)} className="w-full text-left flex items-start gap-2.5 p-2.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl hover:border-[#B3E0F2] transition-colors cursor-pointer" data-testid={`activity-row-${i}`}>
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#5CB3DC] shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[11px] font-extrabold text-[#374151]">
                      <span className="text-[#5CB3DC] uppercase tracking-wide mr-1.5">{a.action}</span>
                      {a.details}
                    </p>
                    <p className="text-[10px] text-[#9CA3AF] font-medium">{fmtDateTime(a.ts)}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
          {logSel && (
            <LogDetailModal
              title={logSel.action || "Activity"}
              fields={[
                { label: "Action", value: logSel.action },
                { label: "Details", value: logSel.details },
                { label: "Time", value: logSel.ts ? fmtDateTime(logSel.ts) : "—" },
                { label: "Actor", value: logSel.actor || logSel.actor_email || "—" },
              ]}
              json={logSel.meta && Object.keys(logSel.meta).length ? logSel.meta : null}
              onClose={() => setLogSel(null)}
            />
          )}
        </div>

        {isSelf ? (
          <p className="text-[11px] text-[#9CA3AF] font-medium text-center" data-testid="profile-self-note">This is your own account — moderation actions are disabled.</p>
        ) : confirmDelete ? (
          <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl" data-testid="delete-confirm-box">
            <p className="text-xs font-extrabold text-rose-500 mb-1">Permanently delete {user.name || user.email}?</p>
            <p className="text-[11px] text-rose-400 font-medium mb-3">This removes the account forever and cannot be undone.</p>
            <div className="flex gap-2">
              <button
                onClick={() => act(() => onAction("delete"))}
                disabled={busy}
                data-testid="delete-confirm-btn"
                className="flex-1 px-4 py-2.5 bg-rose-500 hover:bg-rose-600 text-white rounded-full text-xs font-bold transition-colors disabled:opacity-50"
              >
                {busy ? "Deleting..." : "Yes, Delete Forever"}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                data-testid="delete-cancel-btn"
                className="px-4 py-2.5 bg-white border border-rose-200 text-rose-400 rounded-full text-xs font-bold hover:bg-rose-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            {user.status === "suspended" ? (
              <button
                onClick={() => act(() => onAction("reactivate"))}
                disabled={busy}
                data-testid="reactivate-user-btn"
                className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#E2F4E1] hover:bg-[#C9EAC7] border border-[#BEE3BD] text-[#2F6031] rounded-full text-xs font-bold transition-colors disabled:opacity-50"
              >
                <RotateCcw className="w-3.5 h-3.5" /> {busy ? "Working..." : "Reactivate"}
              </button>
            ) : (
              <button
                onClick={() => act(() => onAction("suspend"))}
                disabled={busy}
                data-testid="suspend-user-btn"
                className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#FFF2D1] hover:bg-[#F5E3B3] border border-[#F5E3B3] text-[#A08244] rounded-full text-xs font-bold transition-colors disabled:opacity-50"
              >
                <Ban className="w-3.5 h-3.5" /> {busy ? "Working..." : "Suspend"}
              </button>
            )}
            <button
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
              data-testid="delete-user-btn"
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-500 rounded-full text-xs font-bold transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete Account
            </button>
          </div>
        )}
        {!isSelf && !confirmDelete && canManageLive && user.role !== "master" && (
          <button
            onClick={() => act(() => onAction(user.live_banned ? "live-unban" : "live-ban"))}
            disabled={busy}
            data-testid="live-ban-btn"
            title={user.live_banned ? "Restore this account's live hosting privileges" : "Suspend live hosting — ends all their active streams instantly and blocks future ones"}
            className={`mt-2 w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-bold transition-colors disabled:opacity-50 ${
              user.live_banned
                ? "bg-[#E6F2FA] hover:bg-[#D2E9F5] border border-[#B3E0F2] text-[#3D7E9E]"
                : "bg-[#374151] hover:bg-[#1F2937] text-white"
            }`}
          >
            <Video className="w-3.5 h-3.5" /> {busy ? "Working..." : user.live_banned ? "Restore Live Hosting" : "Suspend Live Hosting"}
          </button>
        )}
      </div>
    </div>
  );
};

export default function UserDirectory({ scope = "master", currentUserEmail }) {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [checked, setChecked] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (role !== "all") params.set("role", role);
      if (status !== "all") params.set("status", status);
      const res = await fetch(`${API}/api/directory/users?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("unauthorized");
      setUsers((await res.json()).users);
    } catch {
      toast.error("User directory unavailable.");
    }
  }, [q, role, status]);

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  // "Contacts" quick-action on the admin header expands + scrolls here
  useEffect(() => {
    const open = () => {
      setCollapsed(false);
      setTimeout(() => document.getElementById("directory-search-input")?.focus(), 150);
    };
    window.addEventListener("nt-open-directory", open);
    return () => window.removeEventListener("nt-open-directory", open);
  }, []);

  const action = async (kind) => {
    if (!selected) return;
    const liveBan = kind === "live-ban" || kind === "live-unban";
    const url = liveBan
      ? `${API}/api/master/live-ban`
      : kind === "delete"
        ? `${API}/api/directory/users/${selected.id}`
        : `${API}/api/directory/users/${selected.id}/${kind}`;
    const res = await fetch(
      url,
      liveBan
        ? { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: selected.email, banned: kind === "live-ban" }) }
        : { method: kind === "delete" ? "DELETE" : "POST", credentials: "include" }
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      toast.error(typeof body.detail === "string" ? body.detail : "Action failed");
      return;
    }
    toast.success(
      kind === "delete"
        ? `${selected.name || selected.email} permanently deleted.`
        : liveBan
          ? `${selected.name || selected.email} ${kind === "live-ban" ? `banned from live hosting — ${body.ended_streams || 0} stream(s) ended` : "can host live events again"}.`
          : `${selected.name || selected.email} ${kind === "suspend" ? "suspended" : "reactivated"}.`
    );
    setSelected(null);
    load();
  };

  const toggleCheck = (id) => setChecked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const exitSelect = () => {
    setSelectMode(false);
    setChecked([]);
    setConfirmBulkDelete(false);
  };

  const bulk = async (kind) => {
    setBulkBusy(true);
    try {
      const res = await fetch(`${API}/api/directory/users/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ids: checked, action: kind }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Bulk action failed");
      const ok = body.succeeded?.length ?? 0;
      const bad = body.failed?.length ?? 0;
      toast.success(`Bulk ${kind}: ${ok} succeeded${bad ? `, ${bad} skipped` : ""}.`);
      exitSelect();
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBulkBusy(false);
    }
  };

  const messageSelected = async () => {
    const emails = (users || []).filter((u) => checked.includes(u.id)).map((u) => u.email).filter(Boolean);
    if (!emails.length) return;
    setBulkBusy(true);
    try {
      const res = await fetch(`${API}/api/chat/threads`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Could not start conversation");
      toast.success(emails.length > 1 ? `Secure group chat started with ${emails.length} people.` : "Secure conversation opened.");
      sessionStorage.setItem("nt_view", "care");
      window.location.assign("/");
    } catch (err) {
      toast.error(err.message);
      setBulkBusy(false);
    }
  };

  const exportCsv = () => {
    const rows = (users || []).filter((u) => checked.includes(u.id));
    const header = "Name,Email,Role,Status,Joined,Last Login,Sign-in Method";
    const lines = rows.map((u) =>
      [u.name, u.email, ROLE_LABELS[u.role] || u.role, u.status || "active", fmtDate(u.created_at), u.last_login ? fmtDateTime(u.last_login) : "Never", u.auth_provider === "google" ? "Google" : "Email & Password"]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `nesttrack-users-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${rows.length} users to CSV.`);
  };

  return (
    <div className={`${ccCard} p-6`} data-testid="user-directory" id="user-directory">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <button
          onClick={() => setCollapsed(!collapsed)}
          data-testid="directory-collapse-toggle"
          className="text-base font-extrabold font-heading flex items-center gap-2.5 text-[#374151] hover:text-[#5CB3DC] transition-colors"
        >
          <span className="w-9 h-9 rounded-full bg-[#E6F2FA] flex items-center justify-center">
            <Users2 className="w-4 h-4 text-[#5CB3DC]" />
          </span>
          User Directory ({users?.length ?? 0})
          {collapsed ? <ChevronDown className="w-4 h-4 text-[#9CA3AF]" /> : <ChevronUp className="w-4 h-4 text-[#9CA3AF]" />}
        </button>
        <div className="flex items-center gap-2">
          <p className="text-[11px] text-[#9CA3AF] font-medium hidden sm:block">
            {scope === "master" ? "Every account on NestTrack — global account info only." : "Accounts linked to your facility."}
          </p>
          <button
            onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}
            data-testid="directory-select-toggle"
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold border transition-colors ${
              selectMode ? "bg-[#374151] text-white border-[#374151]" : "bg-white text-[#374151] border-[#E6F2FA] hover:bg-[#E6F2FA]"
            }`}
          >
            {selectMode ? <X className="w-3.5 h-3.5" /> : <CheckSquare className="w-3.5 h-3.5" />}
            {selectMode ? "Cancel" : "Select"}
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2.5 mb-4">
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            id="directory-search-input"
            value={q}
            onFocus={() => setCollapsed(false)}
            onChange={(e) => { setQ(e.target.value); setCollapsed(false); }}
            placeholder="Search by name or email..."
            className={`${ccInput} pl-9`}
            data-testid="directory-search-input"
          />
        </div>
        <select value={role} onChange={(e) => { setRole(e.target.value); setCollapsed(false); }} className={`${ccInput} sm:w-44`} data-testid="directory-role-filter">
          {ROLE_FILTERS.map((r) => (
            <option key={r} value={r}>{r === "all" ? "All Roles" : ROLE_LABELS[r]}</option>
          ))}
        </select>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setCollapsed(false); }} className={`${ccInput} sm:w-36`} data-testid="directory-status-filter">
          {STATUS_FILTERS.map((s) => (
            <option key={s} value={s}>{s === "all" ? "Any Status" : s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
      </div>

      {!collapsed && selectMode && (
        <div className="mb-4 p-3.5 bg-[#E6F2FA]/50 border border-[#B3E0F2] rounded-2xl flex items-center justify-between flex-wrap gap-2.5" data-testid="bulk-action-bar">
          <p className="text-xs font-extrabold text-[#3D7E9E]" data-testid="bulk-selected-count">
            {checked.length} selected
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={messageSelected}
              disabled={!checked.length || bulkBusy}
              data-testid="bulk-message-btn"
              className="flex items-center gap-1.5 px-3.5 py-2 bg-[#374151] hover:bg-[#1F2937] text-white rounded-full text-[11px] font-bold transition-colors disabled:opacity-50"
            >
              <MessageCircle className="w-3 h-3" /> Message
            </button>
            <button
              onClick={() => bulk("suspend")}
              disabled={!checked.length || bulkBusy}
              data-testid="bulk-suspend-btn"
              className="flex items-center gap-1.5 px-3.5 py-2 bg-[#FFF2D1] hover:bg-[#F5E3B3] border border-[#F5E3B3] text-[#A08244] rounded-full text-[11px] font-bold transition-colors disabled:opacity-50"
            >
              <Ban className="w-3 h-3" /> Suspend
            </button>
            <button
              onClick={() => bulk("reactivate")}
              disabled={!checked.length || bulkBusy}
              data-testid="bulk-reactivate-btn"
              className="flex items-center gap-1.5 px-3.5 py-2 bg-[#E2F4E1] hover:bg-[#C9EAC7] border border-[#BEE3BD] text-[#2F6031] rounded-full text-[11px] font-bold transition-colors disabled:opacity-50"
            >
              <RotateCcw className="w-3 h-3" /> Reactivate
            </button>
            <button
              onClick={exportCsv}
              disabled={!checked.length || bulkBusy}
              data-testid="bulk-export-btn"
              className="flex items-center gap-1.5 px-3.5 py-2 bg-white hover:bg-[#E6F2FA] border border-[#B3E0F2] text-[#3D7E9E] rounded-full text-[11px] font-bold transition-colors disabled:opacity-50"
            >
              <Download className="w-3 h-3" /> Export CSV
            </button>
            {confirmBulkDelete ? (
              <>
                <button
                  onClick={() => bulk("delete")}
                  disabled={bulkBusy}
                  data-testid="bulk-delete-confirm-btn"
                  className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full text-[11px] font-bold transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-3 h-3" /> {bulkBusy ? "Deleting..." : `Delete ${checked.length} Forever?`}
                </button>
                <button
                  onClick={() => setConfirmBulkDelete(false)}
                  data-testid="bulk-delete-cancel-btn"
                  className="px-3.5 py-2 bg-white border border-rose-200 text-rose-400 rounded-full text-[11px] font-bold hover:bg-rose-50 transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setConfirmBulkDelete(true)}
                disabled={!checked.length || bulkBusy}
                data-testid="bulk-delete-btn"
                className="flex items-center gap-1.5 px-3.5 py-2 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-500 rounded-full text-[11px] font-bold transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            )}
          </div>
        </div>
      )}

      {!collapsed && (users === null ? (
        <p className="text-xs text-[#9CA3AF] font-medium py-6 text-center" data-testid="directory-loading">Loading users...</p>
      ) : users.length === 0 ? (
        <p className="text-xs text-[#9CA3AF] font-medium py-6 text-center" data-testid="directory-empty">No users match your search.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5" data-testid="directory-grid">
          {users.map((u) => {
            const isChecked = checked.includes(u.id);
            return (
              <button
                key={u.id}
                onClick={() => (selectMode ? toggleCheck(u.id) : setSelected(u))}
                data-testid={`directory-user-${u.email}`}
                className={`text-left p-3.5 border rounded-2xl flex items-center gap-3 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer ${
                  selectMode && isChecked
                    ? "bg-[#E6F2FA] border-[#5CB3DC]"
                    : "bg-[#FFF9F2] border-[#FCE6E9] hover:bg-[#E6F2FA]/40 hover:border-[#B3E0F2]"
                }`}
              >
                {selectMode && (
                  <span className="shrink-0 text-[#5CB3DC]" data-testid={`directory-check-${u.email}`}>
                    {isChecked ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4 text-[#9CA3AF]" />}
                  </span>
                )}
                <div className="w-10 h-10 rounded-full bg-[#374151] overflow-hidden flex items-center justify-center text-white text-xs font-black font-heading shrink-0">
                  {u.avatar ? <img src={u.avatar} alt={u.name} className="w-full h-full object-cover" /> : (u.name || "?").charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-extrabold text-[#374151] truncate">{u.name || "Unnamed"}</p>
                  <p className="text-[11px] text-[#9CA3AF] truncate">{u.email}</p>
                  <p className="text-[10px] font-bold text-[#5CB3DC] uppercase tracking-wide mt-0.5">{ROLE_LABELS[u.role] || u.role}</p>
                </div>
                <div className="flex flex-col gap-1 shrink-0" data-testid={`directory-contact-${u.email}`}>
                  {u.phone ? (
                    <a href={`tel:${u.phone}`} onClick={(e) => e.stopPropagation()} title={`Call ${u.phone}`} data-testid={`directory-call-${u.email}`} className="w-7 h-7 rounded-full bg-[#E2F4E1] text-[#2F6031] flex items-center justify-center hover:bg-[#D2EBD1] transition-colors">
                      <Phone className="w-3.5 h-3.5" />
                    </a>
                  ) : (
                    <span title="No phone on file" data-testid={`directory-call-${u.email}`} className="w-7 h-7 rounded-full bg-[#FFF9F2] text-[#E5E7EB] flex items-center justify-center cursor-not-allowed">
                      <Phone className="w-3.5 h-3.5" />
                    </span>
                  )}
                  {u.phone ? (
                    <a href={`sms:${u.phone}`} onClick={(e) => e.stopPropagation()} title={`Text ${u.phone}`} data-testid={`directory-text-${u.email}`} className="w-7 h-7 rounded-full bg-[#FCE6E9] text-[#B2607F] flex items-center justify-center hover:bg-[#F8C8DC] transition-colors">
                      <MessageCircle className="w-3.5 h-3.5" />
                    </a>
                  ) : (
                    <span title="No phone on file" data-testid={`directory-text-${u.email}`} className="w-7 h-7 rounded-full bg-[#FFF9F2] text-[#E5E7EB] flex items-center justify-center cursor-not-allowed">
                      <MessageCircle className="w-3.5 h-3.5" />
                    </span>
                  )}
                  <a href={`mailto:${u.email}`} onClick={(e) => e.stopPropagation()} title={`Email ${u.email}`} data-testid={`directory-email-${u.email}`} className="w-7 h-7 rounded-full bg-[#E6F2FA] text-[#3D7E9E] flex items-center justify-center hover:bg-[#D6EBF7] transition-colors">
                    <Mail className="w-3.5 h-3.5" />
                  </a>
                </div>
                <StatusBadge status={u.status} />
              </button>
            );
          })}
        </div>
      ))}

      <UserProfileModal user={selected} onClose={() => setSelected(null)} onAction={action} currentUserEmail={currentUserEmail} canManageLive={scope === "master"} />
    </div>
  );
}
